import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '';

if (!BASE_URL) {
  console.warn('[FileShare] VITE_API_URL is not set. Create a .env file with VITE_API_URL=https://your-api-url');
}

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach session token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('sessionToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  console.debug(`[API] ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Surface error messages; bubble up 401 for the app to handle
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message || 'Unexpected error';
    console.error('[API] Error:', message, error);
    return Promise.reject(Object.assign(new Error(message), { status: error.response?.status }));
  }
);

// ── Auth ─────────────────────────────────────────────────────────────────────

/**
 * Exchange GitLab OAuth code for a session token.
 * @param {string} code  — code from GitLab redirect
 * @param {string} redirectUri — must match what was registered in GitLab
 * @returns {{ sessionToken: string, user: object }}
 */
export async function exchangeOAuthCode(code, redirectUri) {
  const res = await apiClient.post('/auth/callback', { code, redirectUri });
  return res.data;
}

/**
 * Get current user's profile and storage quota.
 * @returns {{ userId, username, email, name, usedStorage, storageQuota }}
 */
export async function getCurrentUser() {
  const res = await apiClient.get('/me');
  return res.data;
}

// ── Files ─────────────────────────────────────────────────────────────────────

/**
 * Request a presigned PUT URL to upload a file (auth required).
 * @param {string} [password]  optional download password (plaintext — hashed server-side)
 * @returns {{ uploadUrl, fileId, shareToken, fileName, passwordProtected }}
 */
export async function requestUploadUrl(fileName, fileSize, contentType, password) {
  const body = { fileName, fileSize, contentType };
  if (password) body.password = password;
  const res = await apiClient.post('/upload', body);
  return res.data;
}

/**
 * Upload file bytes directly to S3 via presigned PUT URL.
 * @param {string} uploadUrl
 * @param {File} file
 * @param {(percent: number) => void} onProgress
 */
export async function uploadFileToS3(uploadUrl, file, onProgress) {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: (e) => {
      if (e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
}

/**
 * List the authenticated user's files.
 * @returns {{ files: Array, count: number }}
 */
export async function listFiles() {
  const res = await apiClient.get('/files');
  return res.data;
}

/**
 * Delete a file by fileId (auth + ownership required).
 */
export async function deleteFile(fileId) {
  const res = await apiClient.delete(`/files/${fileId}`);
  return res.data;
}

/**
 * Get download info for a shared file by shareToken — NO auth required.
 * If the file is password-protected and no password is supplied,
 * returns { requiresPassword: true, fileName, fileSize, ... } (no downloadUrl).
 * @param {string} shareToken
 * @param {string} [password]  optional plaintext password
 * @returns {{ downloadUrl?, requiresPassword?, fileName, fileSize, ... }}
 */
export async function getDownloadInfo(shareToken, password) {
  const params = password ? { password } : {};
  const res = await apiClient.get(`/download/${shareToken}`, { params });
  return res.data;
}
